import {inject, Injectable} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {ErrorService} from "./api/error/error.service";
import {LoggerService} from "./api/logging/logger.service";
import * as Sentry from "@sentry/angular";
import {SentryErrorHandler} from "@sentry/angular";
import {extractErrorMessage} from "./utils";

@Injectable()
export class GlobalErrorHandler extends SentryErrorHandler {
  private readonly errorService = inject(ErrorService);
  private readonly logger = inject(LoggerService);

  constructor() {
    super({logErrors: true});
  }

  override handleError(error: unknown): void {
    // HttpErrorResponses are already captured — and correctly grouped by status+path —
    // by HttpErrorInterceptor before they are re-thrown (see http-error.interceptor.ts).
    // For the uncaught ones that still reach here we must NOT re-capture (that would
    // double the Sentry event volume), and must NOT hand the raw response to
    // super.handleError: Sentry's base handler has no name/stack to work with and would
    // collapse every HTTP error into a single issue. So fall through to the local
    // logging / UI error banner below without sending a second event.
    if (!(error instanceof HttpErrorResponse)) {
      // Sentry's handler: extracts Angular-wrapped errors,
      // runs captureException outside zone, adds mechanism
      super.handleError(error);
    }

    // Log to Sentry as fallback if captureException fails
    const message = extractErrorMessage(error);
    Sentry.logger.error(`Unhandled error: ${message}`);

    // Register locally for UI error banner
    this.logger.error('An error occurred:', message);
    this.errorService.registerError(message);
  }
}
